import React from 'react';
import { ColumnDef, flexRender, getCoreRowModel, useReactTable, getPaginationRowModel } from '@tanstack/react-table';

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { DataTablePagination } from '../ui/data-table-pagination';

interface DataTableProps<TData extends { orderId: string }, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  pagination?: {
    current: number;
    pageSize: number;
    total: number;
  };
  onPaginationChange?: (pagination: { current: number; pageSize: number }) => void;
}

export function DataTable<TData extends { orderId: string }, TValue>({
  columns,
  data,
  pagination,
  onPaginationChange,
}: DataTableProps<TData, TValue>) {
  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: !!pagination,
    pageCount: pagination ? Math.ceil(pagination.total / pagination.pageSize) : 0,
    state: {
      pagination: pagination
        ? {
            pageIndex: pagination.current - 1,
            pageSize: pagination.pageSize,
          }
        : {
            pageIndex: 0,
            pageSize: 10,
          },
    },

    onPaginationChange: (updater) => {
      if (onPaginationChange && pagination) {
        const newState =
          typeof updater === 'function'
            ? updater({
                pageIndex: pagination.current - 1,
                pageSize: pagination.pageSize,
              })
            : updater;

        onPaginationChange({
          current: newState.pageIndex + 1,
          pageSize: newState.pageSize,
        });
      }
    },
  });

  return (
    <div className="rounded-md border">
      <Table className="[&_th]:px-4 [&_td]:px-4">
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead key={header.id}>
                    {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'} className="h-18">
                {row.getVisibleCells().map((cell) => (
                  <TableCell key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                No results.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
      <DataTablePagination table={table} total={pagination.total} />
    </div>
  );
}
